
	import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
	import { namespace } from "vuex-class";
	import Tree from "./Tree.vue";
	import { SiteTreeNode, Group } from "@/store/eventqueue/types";
	import VuePerfectScrollbar from "vue-perfect-scrollbar";
	import { clearTimeout } from "timers";
	import AreaFinderStatus from "@/components/AreaFinderStatus.vue";
import { Region } from '@/store/types';

	const Eventqueue = namespace("eventqueue");

	@Component({
		components: {
			tree: Tree,
			VuePerfectScrollbar: VuePerfectScrollbar,
			"area-finder-status": AreaFinderStatus
		}
	})
	export default class AreaFinder extends Vue {
		$refs!: {
			areaTreeScrollBar: any;
		};

		@Eventqueue.Getter("getSiteTree") tree: SiteTreeNode[];
		@Eventqueue.Getter getTreeSelectedGroup: Group;
		@Eventqueue.Getter getSearchSelectedGroup: Group;

		@Eventqueue.Action collapseSiteTree: any;
		@Eventqueue.Action sitesTreeGetWithRegion: ({ multiTenant: boolean, regionURL: string }) => Promise<void>;

		@Eventqueue.Mutation setSiteTreeFilter: (search: string) => void;
		@Eventqueue.Mutation setTreeGroupSelected: any;
		@Eventqueue.Mutation setSearchGroupSelected: any;
		@Eventqueue.Mutation setSiteTreeFilterOff: any;
		@Eventqueue.Mutation setSiteTreeFilterOn: any;
		
		@Prop(Boolean) collapsed: boolean;
		@Prop(Number) collapsedLevel: number;
		@Prop({ type: Boolean, default: false }) showSystemArea: boolean;
		@Prop({ type: Boolean, default: false }) multiTenant: boolean;
		@Prop({ default: null }) public region: Region | null;

		public areaSearch: string = "";
		public searchResults: any[] = [];
		public clicks: number = 0;
		public clickTimer: number | null = null;
		private selectedGroupId: number = null;
		public sitesLoading: boolean = false;

		public async beforeMount() {
			try {
				this.sitesLoading = true;
				await this.loadSiteTree();
				this.sitesLoading = false;
			}
			catch {
				this.sitesLoading = false;
			}			
		}

		@Watch("region")
		private onRegionChanged() {
			this.loadSiteTree();
		}

		public async loadSiteTree(){
			await this.sitesTreeGetWithRegion({ multiTenant: this.multiTenant, regionURL: this.region?.url });
		}

		private lastFilter: string = "";
		private shortcutKey: string = "";

		public get hasSelectedGroup() {
			let selectedGroup = this.selectedGroup;
			return selectedGroup.groupID !== undefined;
		}

		public get selectedGroup() {
			if (this.areaSearch == "") {
				return this.getTreeSelectedGroup;
			} else {
				return this.getSearchSelectedGroup;
			}
		}

		@Emit("onSelected")
		public treeGroupSelected(nodeGroup: SiteTreeNode) {
			this.setTreeGroupSelected({
				group: nodeGroup.group
			});
			this.$emit("input", nodeGroup);
			this.selectedGroupId = nodeGroup.group.groupID;
			return nodeGroup;
		}

		public treeGroupDoubleClick(nodeGroup: SiteTreeNode) {
			this.setTreeGroupSelected({
				group: nodeGroup.group
			});
			this.$emit("groupDoubleClicked", nodeGroup.group);
		}

		@Watch("tree")
		public onTreeChange() {
			if (this.collapsed && this.collapsedLevel) {
				this.collapseSiteTree({
					groups: this.tree,
					lvl: 1,
					collapsedLevel: this.collapsedLevel
				});
			}
			this.$refs.areaTreeScrollBar.update();
		}

		/**
		 * Returns true if the given node has sub-nodes.
		 */
		public nodeHasChildren(nodeGroup: SiteTreeNode): boolean {
			return (
				typeof nodeGroup.subGroups != "undefined" &&
				nodeGroup.subGroups != null &&
				nodeGroup.subGroups.length > 0 &&
				nodeGroup.subGroups.reduce((curr, node) => curr || node.show, false)
			);
		}

		/**
		 * Selects the first visible site node in the tree, if there is one.
		 */
		public selectFirstNode() {
			const getChild = (nodeGroups: SiteTreeNode[]) => {
				for (var child of nodeGroups) {
					if (child.show) {
						if (this.nodeHasChildren(child)) {
							return getChild(child.subGroups);
						} else {
							return child;
						}
					}
				}

				return null;
			};

			var firstNode = getChild(this.tree);
			if (firstNode == null) return;
			else {
				this.treeGroupSelected({
					node: firstNode,
					group: firstNode.group
				});
			}
		}

		@Watch("areaSearch")
		public onAreaSearchChange(value: string, oldValue: string) {
			if (value != oldValue) {
				this.setSiteTreeFilter(value);
			}
		}

		public get anyVisibleSites() {
			return this.tree.reduce((curr, prev) => curr || prev.show, false);
		}

		private systemArea: SiteTreeNode = {
			group: {
				groupID: 0,
				parentGroupID: null,
				title: "System Area",
				timeZoneID: 0,
				status: 0,
				selected: true
			},
			subGroups: null,
			isOpen: false
		}
	}
